import {
  BOUNDARY_LINE_HIT_PIXELS,
  BOUNDARY_VERTEX_HIT_PIXELS,
  findNearestVertex,
  isPointInPolygon,
  toLngLatRing,
} from "context/PlotMap/boundaryUtils";
import type {
  BoundaryPoint,
  MapViewportProjector,
  ScreenPoint,
} from "context/PlotMap/geo";
import {
  boundaryRing,
  collectUniqueEdges,
  getVertexPoint,
} from "context/PlotMap/gridMeshGeometry";
import { collectRowDividerEdges } from "context/PlotMap/gridRowSplit";
import type {
  GridEdgeRef,
  GridMesh,
  GridVertex,
} from "context/PlotMap/gridMeshTypes";

/** Mesh vertex id whose projection is within `hitPixels` of the screen point. */
export const findMeshVertexIdAtScreenPoint = (
  map: MapViewportProjector,
  vertices: Record<string, GridVertex>,
  screenPoint: ScreenPoint,
  hitPixels: number = BOUNDARY_VERTEX_HIT_PIXELS,
): string | null => {
  const ids = Object.keys(vertices);
  const points = ids.map((id) => {
    const vertex = vertices[id];
    return {
      latitude: vertex?.latitude ?? 0,
      longitude: vertex?.longitude ?? 0,
    };
  });
  const nearest = findNearestVertex(map, points, screenPoint);
  if (nearest == null || nearest.distancePx > hitPixels) {
    return null;
  }

  return ids[nearest.index] ?? null;
};

/**
 * Closest point on a geo segment measured in screen pixels: project endpoints,
 * clamp t ∈ [0,1] in pixel space, then interpolate the same t in lng/lat.
 */
export const closestPointOnSegmentScreen = (
  map: MapViewportProjector,
  start: BoundaryPoint,
  end: BoundaryPoint,
  screenPoint: ScreenPoint,
): { distancePx: number; point: BoundaryPoint } => {
  const startPx = map.project([start.longitude, start.latitude]);
  const endPx = map.project([end.longitude, end.latitude]);
  const deltaX = endPx.x - startPx.x;
  const deltaY = endPx.y - startPx.y;
  const lengthSq = deltaX * deltaX + deltaY * deltaY;
  if (lengthSq === 0) {
    return {
      distancePx: Math.hypot(
        startPx.x - screenPoint.x,
        startPx.y - screenPoint.y,
      ),
      point: start,
    };
  }

  // 1) Clamp t in screen space so hit-testing matches what the user sees.
  const t = Math.max(
    0,
    Math.min(
      1,
      ((screenPoint.x - startPx.x) * deltaX +
        (screenPoint.y - startPx.y) * deltaY) /
        lengthSq,
    ),
  );
  const closestPx = {
    x: startPx.x + t * deltaX,
    y: startPx.y + t * deltaY,
  };

  // 2) Reuse the same t in lng/lat for the geo point under that pixel.
  return {
    distancePx: Math.hypot(
      closestPx.x - screenPoint.x,
      closestPx.y - screenPoint.y,
    ),
    point: {
      latitude: start.latitude + t * (end.latitude - start.latitude),
      longitude: start.longitude + t * (end.longitude - start.longitude),
    },
  };
};

/** Nearest unique mesh edge within `hitPixels` of the screen point, or null. */
export const findEdgeAtScreenPoint = (
  map: MapViewportProjector,
  mesh: GridMesh,
  screenPoint: ScreenPoint,
  hitPixels: number = BOUNDARY_LINE_HIT_PIXELS,
): GridEdgeRef | null => {
  let bestDistancePx = Number.POSITIVE_INFINITY;
  let bestEdge: GridEdgeRef | null = null;

  for (const edge of collectUniqueEdges(mesh.cells)) {
    const start = getVertexPoint(mesh.vertices, edge.startVertexId);
    const end = getVertexPoint(mesh.vertices, edge.endVertexId);
    if (start == null || end == null) {
      continue;
    }

    const closest = closestPointOnSegmentScreen(map, start, end, screenPoint);
    if (
      closest.distancePx > hitPixels ||
      closest.distancePx >= bestDistancePx
    ) {
      continue;
    }

    bestDistancePx = closest.distancePx;
    bestEdge = edge;
  }

  if (bestEdge != null) {
    return bestEdge;
  }

  for (const edge of collectRowDividerEdges(mesh)) {
    const start = getVertexPoint(mesh.vertices, edge.startVertexId);
    const end = getVertexPoint(mesh.vertices, edge.endVertexId);
    if (start == null || end == null) {
      continue;
    }

    const closest = closestPointOnSegmentScreen(map, start, end, screenPoint);
    if (
      closest.distancePx > hitPixels ||
      closest.distancePx >= bestDistancePx
    ) {
      continue;
    }

    bestDistancePx = closest.distancePx;
    bestEdge = edge;
  }

  return bestEdge;
};

/**
 * Topmost cell (last in mesh.cells) whose ring contains `point`, or null.
 * Later cells win so overlapping/merged geometry prefers the paint order.
 */
export const findCellAtPoint = (
  mesh: GridMesh,
  point: BoundaryPoint,
): string | null => {
  for (let index = mesh.cells.length - 1; index >= 0; index -= 1) {
    const cell = mesh.cells[index];
    if (cell == null) {
      continue;
    }
    const ring = toLngLatRing(boundaryRing(cell.vertexIds, mesh.vertices));
    if (ring.length >= 3 && isPointInPolygon(point, ring)) {
      return cell.id;
    }
  }

  return null;
};

/**
 * Cell ids that intersect an axis-aligned lng/lat bbox: any vertex inside, or
 * else the cell centroid inside (catches cells that swallow the box).
 */
export const cellsIntersectingBbox = (
  mesh: GridMesh,
  bbox: [[number, number], [number, number]],
): string[] => {
  const [[minLng, minLat], [maxLng, maxLat]] = bbox;
  const selected: string[] = [];

  mesh.cells.forEach((cell) => {
    const ring = toLngLatRing(boundaryRing(cell.vertexIds, mesh.vertices));
    if (ring.length < 3) {
      return;
    }

    // Prefer any vertex inside the box.
    const anyVertexInside = ring.some(
      ([longitude, latitude]) =>
        longitude >= minLng &&
        longitude <= maxLng &&
        latitude >= minLat &&
        latitude <= maxLat,
    );
    if (anyVertexInside) {
      selected.push(cell.id);
      return;
    }

    // Otherwise accept if the centroid is inside (cell swallows the bbox).
    const centroid = {
      latitude: ring.reduce((sum, [, lat]) => sum + lat, 0) / ring.length,
      longitude: ring.reduce((sum, [lng]) => sum + lng, 0) / ring.length,
    };
    if (
      centroid.longitude >= minLng &&
      centroid.longitude <= maxLng &&
      centroid.latitude >= minLat &&
      centroid.latitude <= maxLat
    ) {
      selected.push(cell.id);
    }
  });

  return selected;
};
